import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MsalProvider } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import App from './App'
import { ThemeProvider } from './context/ThemeContext'
import { msalConfig } from './lib/auth'
// Self-hosted IBM Plex (no Google Fonts in production). Imported before
// index.css so the @font-face rules exist before the base layer names them.
// Latin only, upright only: 300 display, 400 UI, 600 emphasis; Mono 400 for code.
import '@fontsource/ibm-plex-sans/latin-300.css'
import '@fontsource/ibm-plex-sans/latin-400.css'
import '@fontsource/ibm-plex-sans/latin-600.css'
import '@fontsource/ibm-plex-mono/latin-400.css'
import './index.css'

const msalInstance = new PublicClientApplication(msalConfig)

// Make msalInstance available globally for API client
;(window as any).msalInstance = msalInstance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

/**
 * Toaster styled from the Carbon tokens. Every value is a CSS variable, so it
 * swaps with the `.light` class on <html> without a React re-render. Icons
 * follow the Neutral Bad State Rule: success is neutral ink, only an error
 * takes Coral.
 */
const ThemedToaster: React.FC = () => (
  <Toaster
    position="top-right"
    toastOptions={{
      duration: 4000,
      style: {
        background: 'var(--layer-02)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 0,
        boxShadow: 'var(--shadow-menu)',
      },
      success: { iconTheme: { primary: 'var(--text-primary)', secondary: 'var(--layer-02)' } },
      error: { iconTheme: { primary: 'var(--sev-crit)', secondary: 'var(--layer-02)' } },
      loading: { iconTheme: { primary: 'var(--text-primary)', secondary: 'var(--border-subtle)' } },
    }}
  />
)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <App />
            <ThemedToaster />
          </QueryClientProvider>
        </BrowserRouter>
      </MsalProvider>
    </ThemeProvider>
  </React.StrictMode>,
)